import Vue from 'vue'
import { firestore } from 'firebase/app'
import fb from '../../firebase'
import firebase from 'firebase/app'
import { ActionContext } from 'vuex'
import { AdminState, RootState, FirestoreDocSnapper, FirestoreSnapperType, Ordway, Version } from '../types'
import { LegalInfo, IOSSettings, LibratoConfig, TranscodingRegion, Notifications } from '@/types/admin'
import { moduleActionContext, storehelper } from '..'
import { Release } from '@/store/types'
import { Feature } from '@/featureFlags/featureFlags'
import { DeepPartial } from '@/types'

type AdminContext = ActionContext<AdminState, RootState>

// initial state
const state = {
  transcodingregions: {},
  legalinfo: {
    tosUrl: "https://kiswe.com/cloudcast/termsofservice", //Fallback URL
    privacyUrl: "https://kiswe.com/cloudcast/privacy",
    tosLastPublished: {
      seconds: (new Date(2020, 5, 1)).getTime() / 1000 //Fallback of an existing published date
    },
    privacyLastPublished: {
      seconds: (new Date(2020, 5, 1)).getTime() / 1000
    }
  },
  librato: null,
  iOS: new IOSSettings({}),
  release: null,
  featureFlags: new Map(),
  ordway: null,
  notifications: null
} as AdminState

// getters
const getters = {
  transcodingregions: (state) => state.transcodingregions,
  release: (state) => state.release
}

const actions = {
  subscribeAdminProperties (context: AdminContext) {
    const { commit } = adminModuleActionContext(context)
    const snapper: FirestoreDocSnapper = {
      doc: fb.db.collection('admin').doc('transcodingregions'),
      mutation: commit.updateTranscodingRegions,
      type: FirestoreSnapperType.Document
    }
    storehelper.subscribe(`transcodingregions`, snapper)

    const snapper2: FirestoreDocSnapper = {
      doc: fb.db.collection('admin').doc('librato'),
      mutation: commit.updateLibrato,
      type: FirestoreSnapperType.Document
    }
    storehelper.subscribe(`librato`, snapper2)

    const snapper3: FirestoreDocSnapper = {
      doc: fb.db.collection('admin').doc('iOS'),
      mutation: commit.updateIOS,
      type: FirestoreSnapperType.Document
    }
    storehelper.subscribe(`iOSSettings`, snapper3)

    const snapper4: FirestoreDocSnapper = {
      doc: fb.db.collection('admin').doc('featureflags'),
      mutation: commit.updateFeatureFlags,
      type: FirestoreSnapperType.Document
    }
    storehelper.subscribe(`featureflags`, snapper4)

    const snapper5: FirestoreDocSnapper = {
      doc: fb.db.collection('admin').doc('ordway'),
      mutation: commit.updateOrdway,
      type: FirestoreSnapperType.Document
    }
    storehelper.subscribe('ordway', snapper5)

    const snapper6: FirestoreDocSnapper = {
      doc: fb.db.collection('admin').doc('notifications'),
      mutation: commit.updateNotifications,
      type: FirestoreSnapperType.Document
    }
    storehelper.subscribe('notifications', snapper6)
  },
  unsubscribeAdminProperties (_context: AdminContext) {
    storehelper.unsubscribe('transcodingregions')
    storehelper.unsubscribe('librato')
    storehelper.unsubscribe('iOSSettings')
    storehelper.unsubscribe('featureflags')
    storehelper.unsubscribe('ordway')
  },
  async deleteWowzaServer (context: AdminContext, serverId: string) {
    const { state } = adminModuleActionContext(context)
    for (const [regionId, region] of Object.entries(state.transcodingregions)) {
      const keys = Object.keys(region.wrtcserver)
      if (keys.find(key => key === serverId) !== undefined) {
        if (keys.length === 1) throw new Error('Could not delete the only server in a region')
        // const updateData = {}
        // updateData[`${regionId}.wrtcserver.${serverId}`] = fb.firebase.firestore.FieldValue.delete()
        // FIXME (tomc): This is really ugly, can make race condition, but tried for maybe 2h to get this to work
        // with the code above, if you get know what the issue is, please let me know?
        const newTranscodingRegions = JSON.parse(JSON.stringify(state.transcodingregions))
        delete newTranscodingRegions[regionId].wrtcserver[serverId]
        fb.db.collection('admin').doc('transcodingregions').set(newTranscodingRegions)
        return
      }
    }
    throw new Error('Could not find server in any region')
  },
  async addWowzaServerToRegion (context: AdminContext, params: { serverId: string, region: string }) {
    const { state } = adminModuleActionContext(context)
    const region = state.transcodingregions[params.region]
    if (region === undefined) throw new Error('Could not find the region')
    const servers = JSON.parse(JSON.stringify(region.wrtcserver))
    servers[params.serverId] = { url: `wrtc-${params.serverId}.kiswe.com` }
    const data = {
      [params.region]: {
        wrtcserver: servers
      }
    }
    await fb.db.collection('admin').doc('transcodingregions').set(data, { merge: true })
  },
  async updateIOSEnabled (_context: AdminContext, enabled: boolean) {
    await fb.db.collection('admin').doc('iOS').set({ enabled }, {merge: true})
  },
  async clearTranscodingLoadbalancerCache (_context: AdminContext) {
    await fb.db.collection('admin').doc('cloudcast_manager_data').set({
      clear_loadbalancer_cache: true
    }, { merge: true })
  },
  async updateFeatureFlag (_context: AdminContext, params: { feature: Feature, enabled: boolean}) {
    await fb.db.collection('admin').doc('featureflags').set({
      [params.feature]: params.enabled
    }, { merge: true })
  },
  subscribeRelease (context: AdminContext) {
    const { commit } = adminModuleActionContext(context)
    const snapper: FirestoreDocSnapper = {
        doc: fb.db.collection('admin').doc('release'),
        mutation: commit.updateRelease,
        type: FirestoreSnapperType.Document
      }
      storehelper.subscribe('release', snapper)
  },
  unsubscribeRelease () {
    storehelper.unsubscribe('release')
  },
  subscribeLegalInfo (context: AdminContext) {
    const { commit } = adminModuleActionContext(context)
    const snapper: FirestoreDocSnapper = {
        doc: fb.db.collection('admin').doc('legalinfo'),
        mutation: commit.updateLegalInfo,
        type: FirestoreSnapperType.Document
      }
      storehelper.subscribe('legalinfo', snapper)
  },
  unsubscribeLegalInfo () {
    storehelper.unsubscribe('legalinfo')
  },
  async setReleaseFirestoreRules (context: AdminContext, version: string) {
    const { state } = adminModuleActionContext(context)
    if (state.release === null ) throw new Error('No release information to adjust')
    const releaseVersion = state.release.versions[version]
    if (releaseVersion === undefined) throw new Error('Version does not exists')
    const updates: DeepPartial<Release> = { versions: {} }
    Object.entries(state.release.versions).forEach(([versionId, entry]) => {
      if (entry.deploy_firebase?.rules) updates.versions![versionId] = { deploy_firebase : {rules: false} }
      if (versionId === version) updates.versions![versionId] = { deploy_firebase : {rules: true} }
    })
    await fb.db.collection('admin').doc('release').set(updates, { merge: true })
  },
  async setReleaseFirestoreFunctions (context: AdminContext, version: string) {
    const { state } = adminModuleActionContext(context)
    if (state.release === null ) throw new Error('No release information to adjust')
    const releaseVersion = state.release.versions[version]
    if (releaseVersion === undefined) throw new Error('Version does not exists')
    const updates: DeepPartial<Release> = { versions: {} }
    Object.entries(state.release.versions).forEach(([versionId, entry]) => {
      if (entry.deploy_firebase?.functions) updates.versions![versionId] = { deploy_firebase : {functions: false} }
      if (versionId === version) updates.versions![versionId] = { deploy_firebase : {functions: true} }
    })
    await fb.db.collection('admin').doc('release').set(updates, { merge: true })
  },
  async setReleaseManifests (context: AdminContext, version: string) {
    const { state } = adminModuleActionContext(context)
    if (state.release === null ) throw new Error('No release information to adjust')
    const releaseVersion = state.release.versions[version]
    if (releaseVersion === undefined) throw new Error('Version does not exists')
    const updates: DeepPartial<Release> = { versions: {} }
    Object.entries(state.release.versions).forEach(([versionId, entry]) => {
      if (entry.deploy_firebase?.manifests) updates.versions![versionId] = { deploy_firebase : {manifests: false} }
      if (versionId === version) updates.versions![versionId] = { deploy_firebase : {manifests: true} }
    })
    await fb.db.collection('admin').doc('release').set(updates, { merge: true })
  },
  async setReleaseService (context: AdminContext, {version, service}: { version: string, service: string }) {
    const { state } = adminModuleActionContext(context)
    if (state.release === null ) throw new Error('No release information to adjust')
    const releaseVersion = state.release.versions[version]
    if (releaseVersion === undefined) throw new Error('Version does not exists')
    const updates: DeepPartial<Release> = { versions: {} }
    Object.entries(state.release.versions).forEach(([versionId, entry]) => {
      if (versionId === version) updates.versions![versionId] = { services_enabled : { [service]: true} }
      else if (entry.services_enabled === undefined) updates.versions![versionId] = { services_enabled : { [service]: false} }
      else if (entry.services_enabled[service]) updates.versions![versionId] = { services_enabled : { [service]: false} }
    })
    await fb.db.collection('admin').doc('release').set(updates, { merge: true })
  },
  async addNewVersionToRelease (context: AdminContext, version: Version) {
    const { state } = adminModuleActionContext(context)
    if (version.id === '') throw new Error('Version id cannot be empty')
    const versions = state.release?.versions
    if (versions === undefined) return
    if (Object.keys(versions).includes(version.id)) throw new Error('Version number is not unique')
    if (version.branch_back === '') throw new Error('Branch Back cannot be empty')
    if (version.branch_front === '') throw new Error('Branch Front cannot be empty')
    await fb.db.collection('admin').doc('release').set({
      versions: {
        [version.id]: version
      }
    }, { merge: true })
  },
  async deleteReleaseVersion (context: AdminContext, version: string) {
    const { state } = adminModuleActionContext(context)
    if (state.release?.default === version) throw new Error('Cannot delete default version')
    await fb.db.collection('admin').doc('release').set({
      versions: {
        [version]: firebase.firestore.FieldValue.delete()
      }
    }, { merge: true })
  },
  async setDefaultReleaseVersion (context: AdminContext, version: string) {
    const { state } = adminModuleActionContext(context)
    if (state.release?.versions[version] === undefined) throw new Error('Cannot set unknown version default')
    await fb.db.collection('admin').doc('release').set({
      default: version
    }, { merge: true })
  },
  async setNGCVPForRelease (context: AdminContext, { version, ngcvpVersion }: { version: string, ngcvpVersion: string }) {
    const { state } = adminModuleActionContext(context)
    if (state.release?.versions[version] === undefined) throw new Error('Cannot set unknown version default')
    await fb.db.collection('admin').doc('release').set({
      versions: {
        [version]: {
          ngcvp_version: ngcvpVersion
        }
      }
    }, { merge: true })
  }

}

// mutations
export const mutations = {
  updateTranscodingRegions (state: AdminState, doc: firestore.DocumentSnapshot) {
    if (!doc.exists) return
    const regionData = doc.data()
    if (regionData === undefined) return
    state.transcodingregions = {}
    for (const [key, region] of Object.entries(regionData)) {
      Vue.set(state.transcodingregions, key, new TranscodingRegion(region))
    }
  },
  updateLibrato (state: AdminState, doc: firestore.DocumentSnapshot) {
    if (!doc.exists) return
    Vue.set(state, 'librato', doc.data() as LibratoConfig)
  },
  updateFeatureFlags (state: AdminState, doc: firestore.DocumentSnapshot) {
    if (!doc.exists) return
    const data = doc.data()
    if (data === undefined) return
    state.featureFlags = new Map()
    for (const [key, value] of Object.entries(data)) {
      const feature = key as Feature
      if (Object.values(Feature).includes(feature)) {
        state.featureFlags.set(feature, value)
      }
    }
  },
  updateOrdway (state: AdminState, doc: firestore.DocumentSnapshot) {
    if (!doc.exists) return

    const data = doc.data()
    if (data === undefined) return

    const ordway = new Ordway(data)
    state.ordway = ordway
  },
  updateLegalInfo (state: AdminState, doc: firestore.DocumentSnapshot) {
    if (!doc.exists) return
    state.legalinfo = doc.data() as LegalInfo //Improve to avoid possible data mismatch and need for typecasting
  },
  updateIOS (state: AdminState, doc: firestore.DocumentSnapshot) {
    if (!doc.exists) return
    Vue.set(state, 'iOS', new IOSSettings(doc.data()))
  },
  updateRelease (state: AdminState, doc: firestore.DocumentSnapshot) {
    if (!doc.exists) return
    state.release = doc.data() as Release
  },
  updateNotifications (state: AdminState, doc: firestore.DocumentSnapshot) {
    if (!doc.exists) return
    state.notifications = doc.data() as Notifications
  }
}

const adminModule = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
} as const
export default adminModule
export const adminModuleActionContext = context => moduleActionContext(context, adminModule)
